import { GRAYS } from "@eldrun/content";
import { Vector3, type BufferGeometry, type Matrix4 } from "three";

/**
 * Entulho dos blocos que quebram (F90): pedaços tirados dos triângulos do próprio modelo, que já vem sem
 * índice para a malha neon (ADR 0014). Cada pedaço sai do centro de um triângulo, na direção da normal
 * dele, com o cinza do vértice (o mundo é escala de cinza; o neon é de quem causa). Puro desenho do
 * cliente: quem decide que quebrou é o servidor.
 */

/** O modelo que quebra: a geometria, a matriz dela no mundo e o atributo de cor (a paleta da instância). */
export interface DebrisSource {
  readonly geometry: BufferGeometry;
  readonly matrix: Matrix4;
  /** Atributo da cor (`color`, ou `color1`/`color2` na paleta da instância). */
  readonly color?: string;
}

/** Um pedaço: onde nasce, para onde vai (a normal do triângulo), o cinza e o tamanho (m). */
export interface DebrisPiece {
  x: number;
  y: number;
  z: number;
  nx: number;
  ny: number;
  nz: number;
  gray: number;
  size: number;
}

/** Tamanho do pedaço pela área do triângulo, entre estes limites (m). */
const MIN_SIZE = 0.05;
const MAX_SIZE = 0.3;

const a = new Vector3();
const b = new Vector3();
const c = new Vector3();
const ab = new Vector3();
const ac = new Vector3();

/**
 * Até `count` pedaços do modelo, de triângulos espalhados por ele em passo fixo (o mesmo modelo, o mesmo
 * entulho: sem `Math.random`). Triângulos de área quase zero não viram pedaço.
 */
export function debrisPieces(source: DebrisSource, count: number): DebrisPiece[] {
  const position = source.geometry.getAttribute("position");
  if (!position || count <= 0) return [];
  const index = source.geometry.getIndex();
  const corner = (i: number) => (index ? index.getX(i) : i);
  const triangles = Math.floor((index ? index.count : position.count) / 3);
  if (triangles === 0) return [];
  const color = source.geometry.getAttribute(source.color ?? "color") ?? source.geometry.getAttribute("color");
  const n = Math.min(count, triangles);
  const step = triangles / n;
  const out: DebrisPiece[] = [];
  for (let k = 0; k < n; k++) {
    const t = Math.floor((k + 0.5) * step);
    const [i0, i1, i2] = [corner(t * 3), corner(t * 3 + 1), corner(t * 3 + 2)];
    a.fromBufferAttribute(position, i0).applyMatrix4(source.matrix);
    b.fromBufferAttribute(position, i1).applyMatrix4(source.matrix);
    c.fromBufferAttribute(position, i2).applyMatrix4(source.matrix);
    const normal = ab.subVectors(b, a).cross(ac.subVectors(c, a));
    const area = normal.length() / 2;
    if (area < 1e-6) continue;
    normal.normalize();
    let gray: number = GRAYS.medio;
    if (color) {
      // Luminância do vértice (a cor dos modelos é linear), presa à faixa do cinza do mundo.
      const luma = (i: number) => 0.2126 * color.getX(i) + 0.7152 * color.getY(i) + 0.0722 * color.getZ(i);
      gray = Math.min(GRAYS.claro, Math.max(GRAYS.escuro, (luma(i0) + luma(i1) + luma(i2)) / 3));
    }
    out.push({
      x: (a.x + b.x + c.x) / 3,
      y: (a.y + b.y + c.y) / 3,
      z: (a.z + b.z + c.z) / 3,
      nx: normal.x,
      ny: normal.y,
      nz: normal.z,
      gray,
      size: Math.min(MAX_SIZE, Math.max(MIN_SIZE, Math.sqrt(area) * 0.6)),
    });
  }
  return out;
}
